import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  alpha,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GroupIcon from "@mui/icons-material/Group";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import {
  findStatus,
  findPriority,
  findCategory,
  findMyRole,
} from "../constants/taskConstants";

/**
 * DataGrid principal del módulo de tareas.
 *
 * @param {Array} tasks - Lista normalizada
 * @param {boolean} loading
 * @param {number} currentEmployeeId
 * @param {(task) => void} onRowClick - Click en fila → abrir drawer
 * @param {(taskId) => void} onComplete - Checkbox / acción Complete
 * @param {(task) => void} onEdit
 * @param {(taskId) => void} onCancel - Cancelar (status = 29)
 * @param {(taskId) => void} onDelete
 * @param {boolean} [showMyRole] - Mostrar columna myRole (solo en vista "my")
 */
export default function TasksDataGrid({
  tasks,
  loading,
  currentEmployeeId,
  onRowClick,
  onComplete,
  onEdit,
  onCancel,
  onDelete,
  showMyRole = false,
}) {
  const rows = useMemo(
    () =>
      (tasks || []).map((t) => ({
        ...t,
        isMine: String(t.assigneeId) === String(currentEmployeeId),
        isCreator: String(t.creatorId) === String(currentEmployeeId),
      })),
    [tasks, currentEmployeeId]
  );

  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);

  const columns = useMemo(() => {
    const cols = [
      // ── Complete checkbox ─────────────────────────────────────────
      {
        field: "complete",
        headerName: "",
        width: 50,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const isDone = row.status === "completed" || row.status === "cancelled";
          return (
            <Checkbox
              size="small"
              checked={row.status === "completed"}
              disabled={isDone}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onComplete?.(row.id)}
              sx={{ p: 0.5 }}
            />
          );
        },
      },
    ];

    // ── My role (solo en vista "my") ────────────────────────────────
    if (showMyRole) {
      cols.push({
        field: "myRole",
        headerName: "",
        width: 50,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const cfg = findMyRole(row.myRole);
          if (!cfg) return null;
          const Icon =
            row.myRole === "creator"
              ? PersonOutlineIcon
              : row.myRole === "assignee"
              ? PersonIcon
              : GroupIcon;
          return (
            <Tooltip title={cfg.label}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  bgcolor: cfg.bg,
                  border: `1.5px solid ${cfg.color}`,
                }}
              >
                <Icon sx={{ fontSize: 16, color: cfg.color }} />
              </Box>
            </Tooltip>
          );
        },
      });
    }

    cols.push(
      // ── Title + patient ─────────────────────────────────────────────
      {
        field: "title",
        headerName: "Task",
        flex: 1,
        minWidth: 240,
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ py: 0.5 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{
                textDecoration: row.status === "completed" ? "line-through" : "none",
                color: row.status === "cancelled" ? "text.disabled" : "text.primary",
              }}
            >
              {row.title}
            </Typography>
            {row.patientName && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.patientName}
              </Typography>
            )}
          </Stack>
        ),
      },

      // ── Status ──────────────────────────────────────────────────────
      {
        field: "status",
        headerName: "Status",
        width: 130,
        renderCell: ({ value }) => {
          const cfg = findStatus(value);
          if (!cfg) return null;
          return (
            <Chip
              label={cfg.label}
              size="small"
              sx={{
                height: 24,
                fontSize: "0.72rem",
                fontWeight: 600,
                bgcolor: cfg.bg,
                color: cfg.color,
              }}
            />
          );
        },
      },

      // ── Priority ────────────────────────────────────────────────────
      {
        field: "priority",
        headerName: "Priority",
        width: 110,
        renderCell: ({ value }) => {
          const cfg = findPriority(value);
          if (!cfg) return null;
          return (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <FiberManualRecordIcon sx={{ fontSize: 10, color: cfg.color }} />
              <Typography variant="body2" fontWeight={500}>{cfg.label}</Typography>
            </Stack>
          );
        },
      },

      // ── Category ────────────────────────────────────────────────────
      {
        field: "category",
        headerName: "Category",
        width: 160,
        renderCell: ({ value }) => {
          const cfg = findCategory(value);
          if (!cfg) {
            return <Typography variant="body2" color="text.secondary">—</Typography>;
          }
          return (
            <Chip
              label={cfg.label}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.7rem",
                fontWeight: 600,
                bgcolor: cfg.bg,
                color: cfg.color,
              }}
            />
          );
        },
      },

      // ── Due date ────────────────────────────────────────────────────
      {
        field: "dueDate",
        headerName: "Due Date",
        width: 120,
        renderCell: ({ row, value }) => {
          if (!value) return <Typography variant="caption" color="text.disabled">No date</Typography>;
          const isActive = row.status !== "completed" && row.status !== "cancelled";
          const isOverdue = isActive && value < todayStr;
          const isToday = value === todayStr;
          return (
            <Typography
              variant="body2"
              fontWeight={isOverdue ? 700 : 500}
              sx={{
                color: isOverdue ? "#DC2626" : isToday ? "#D97706" : "text.primary",
              }}
            >
              {isToday ? "Today" : value}
            </Typography>
          );
        },
      },

      // ── Assignee ────────────────────────────────────────────────────
      {
        field: "assigneeName",
        headerName: "Assignee",
        width: 180,
        renderCell: ({ value }) => (
          <Typography variant="body2" noWrap color={value ? "text.primary" : "text.disabled"}>
            {value || "Unassigned"}
          </Typography>
        ),
      },

      // ── Created by ──────────────────────────────────────────────────
      {
        field: "createdBy",
        headerName: "Created By",
        width: 160,
        renderCell: ({ value }) => (
          <Typography variant="body2" color="text.secondary" noWrap>{value || "—"}</Typography>
        ),
      },

      // ── Actions ─────────────────────────────────────────────────────
      {
        field: "actions",
        headerName: "",
        width: 50,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <RowActionsMenu
            row={row}
            onEdit={onEdit}
            onCancel={onCancel}
            onDelete={onDelete}
          />
        ),
      },
    );

    return cols;
  }, [showMyRole, todayStr, onComplete, onEdit, onCancel, onDelete]);

  return (
    <Box
      sx={{
        height: 560,
        width: "100%",
        "& .MuiDataGrid-root": {
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
        },
        "& .MuiDataGrid-columnHeaders": {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: 700,
          fontSize: "0.78rem",
          textTransform: "uppercase",
          color: "text.secondary",
          letterSpacing: 0.5,
        },
        "& .MuiDataGrid-row": {
          cursor: "pointer",
          transition: "background-color 0.15s",
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
          },
        },
        "& .MuiDataGrid-cell": {
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={(r) => r.id}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          sorting: { sortModel: [{ field: "dueDate", sort: "asc" }] },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        onRowClick={({ row }) => onRowClick?.(row)}
      />
    </Box>
  );
}

// ── Menú "⋮" por fila ─────────────────────────────────────────────────

function RowActionsMenu({ row, onEdit, onCancel, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const isDone = row.status === "completed" || row.status === "cancelled";
  const canDelete = row.isCreator;

  const open = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const close = (e) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton size="small" onClick={open} sx={{ p: 0.5 }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        <MenuItem
          disabled={isDone}
          onClick={(e) => { close(e); onEdit?.(row); }}
          sx={{ fontSize: "0.85rem" }}
        >
          Edit
        </MenuItem>
        <MenuItem
          disabled={isDone}
          onClick={(e) => { close(e); onCancel?.(row.id); }}
          sx={{ fontSize: "0.85rem" }}
        >
          Cancel
        </MenuItem>
        <MenuItem
          disabled={!canDelete}
          onClick={(e) => { close(e); onDelete?.(row.id); }}
          sx={{ fontSize: "0.85rem", color: canDelete ? "#DC2626" : undefined }}
        >
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}
